import type { DashboardStatusBucket } from './hooks';

/**
 * Fulfillment bucket mapping (M-23/M-56 dashboard truth).
 *
 * The OMA aggregate's delivered/pending fields match ONLY the literal
 * "Delivered"/"Pending" statuses, but a delivered order keeps moving
 * through the money leg of the OMA OrderStatus lifecycle (Delivered →
 * CashCollected → CashInAccount → BalanceUpdated → Paid → Complete), so
 * for an established merchant ~96% of successful orders sit in
 * "Complete" and the naive mapping reported a 44% "delivery rate" for a
 * ~92%-successful book. These sets bucket the real statusBreakdown
 * payload; nothing is invented.
 *
 * Deliberately unmapped (not a delivery outcome, excluded from the four
 * segments AND from the rate): Cancelled, Invalid, ResolutionRequired.
 */
const DELIVERED = new Set([
  'delivered',
  'complete',
  'paid',
  'cashcollected',
  'cashinaccount',
  'balanceupdated',
]);

const IN_PROGRESS = new Set([
  'requested',
  'verified',
  'placed',
  'updated',
  'pending',
  'forpacking',
  'packinginprogress',
  'packed',
  'fulfilled',
  'readyforpickup',
  'pickedup',
  'pickedupbydriver',
  'ondelivery',
]);

const FAILED = new Set(['failed', 'semifailed']);

const RETURNED = new Set([
  'toreturn',
  'returned',
  'inchecking',
  'return_pending',
  'return_in_progress',
]);

export interface FulfillmentBuckets {
  delivered: number;
  inProgress: number;
  failed: number;
  returned: number;
}

export const fulfillmentBuckets = (
  statusBreakdown: DashboardStatusBucket[] | undefined
): FulfillmentBuckets =>
  (statusBreakdown ?? []).reduce(
    (acc, { status, count }) => {
      const s = status.toLowerCase();
      if (DELIVERED.has(s)) acc.delivered += count;
      else if (IN_PROGRESS.has(s)) acc.inProgress += count;
      else if (FAILED.has(s)) acc.failed += count;
      else if (RETURNED.has(s)) acc.returned += count;
      return acc;
    },
    { delivered: 0, inProgress: 0, failed: 0, returned: 0 }
  );

/** % of resolved orders (delivered + failed + returned) that delivered. */
export const deliveryRate = (b: FulfillmentBuckets): number | null => {
  const resolved = b.delivered + b.failed + b.returned;
  return resolved > 0 ? Math.round((b.delivered / resolved) * 100) : null;
};

export type RateSeverity = 'good' | 'warn' | 'bad';

/** Severity thresholds: ≥90 good (green), 70–89 warn (amber), <70 bad (coral). */
export const rateSeverity = (rate: number): RateSeverity => {
  if (rate >= 90) return 'good';
  if (rate >= 70) return 'warn';
  return 'bad';
};
